import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ClerkLoaded,
  ClerkLoading,
  OrganizationSwitcher,
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useAuth,
  useClerk,
  useOrganization,
  useUser,
} from "@clerk/clerk-react";
import { ShieldCheck, AlertCircle, Building2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RoleLegend } from "@/components/RoleLegend";
import {
  MemberRow,
  MemberRowSkeleton,
} from "@/components/MemberRow";
import { GrantDialog } from "@/components/GrantDialog";
import { RevokeDialog } from "@/components/RevokeDialog";
import { listMembers, type Member } from "@/lib/api";
import { adminIsSuperAdmin, adminListOrgs, type AdminOrg } from "@/lib/adminApi";

interface OrgProduct { id: string; name: string; description: string | null; }

async function fetchOrgProducts(getToken: () => Promise<string | null>): Promise<OrgProduct[]> {
  const token = await getToken();
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/org/products", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to load products");
  return res.json() as Promise<OrgProduct[]>;
}

export default function AccessPage() {
  const [params, setParams] = useSearchParams();
  const tool = params.get("tool") ?? "";
  const { getToken } = useAuth();
  const [products, setProducts] = useState<OrgProduct[] | null>(null);

  useEffect(() => {
    if (!tool) return;
    fetchOrgProducts(getToken)
      .then(setProducts)
      .catch(() => {});
  }, [tool, getToken]);

  const toolName = tool
    ? (products?.find((p) => p.id === tool)?.name ?? tool)
    : "";

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <header className="mb-8 flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 text-indigo-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {tool ? `${toolName} — Access Management` : "Access Management"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {tool
              ? "Manage who can use this tool in your organisation."
              : "Select a tool to manage access for."}
          </p>
        </div>
        <ClerkLoaded>
          <SignedIn>
            <UserButton />
          </SignedIn>
        </ClerkLoaded>
      </header>

      <ClerkLoading>
        <div className="space-y-3">
          <div className="h-16 w-full animate-pulse rounded-md bg-slate-100" />
          <div className="h-16 w-full animate-pulse rounded-md bg-slate-100" />
          <div className="h-16 w-full animate-pulse rounded-md bg-slate-100" />
        </div>
      </ClerkLoading>

      <ClerkLoaded>
        <SignedOut>
          <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
            <ShieldCheck className="mb-3 h-10 w-10 text-slate-400" />
            <h2 className="text-base font-semibold text-slate-700">
              Sign in to continue
            </h2>
            <p className="mt-1 mb-4 max-w-sm text-sm text-slate-500">
              You need to be signed in as an organisation admin to manage access.
            </p>
            <SignInButton mode="modal">
              <Button>Sign in</Button>
            </SignInButton>
          </div>
        </SignedOut>

        <SignedIn>
          {tool ? (
            <SignedInBody tool={tool} />
          ) : (
            <ToolPickerBody onSelect={(id) => setParams({ tool: id })} />
          )}
        </SignedIn>
      </ClerkLoaded>
    </div>
  );
}

function ToolPickerBody({ onSelect }: { onSelect: (id: string) => void }) {
  const { getToken } = useAuth();
  const [products, setProducts] = useState<OrgProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrgProducts(getToken)
      .then(setProducts)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load products"));
  }, [getToken]);

  if (error) return (
    <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      {error}
    </div>
  );

  if (!products) return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {[...Array(4)].map((_, i) => (
        <li key={i}>
          <div className="flex w-full items-center gap-3 rounded-md border border-slate-200 bg-white px-5 py-4">
            <div className="h-5 w-5 flex-shrink-0 animate-pulse rounded bg-slate-200" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3.5 w-32 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-16 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  if (products.length === 0) return (
    <p className="text-sm text-slate-400 py-8 text-center">No tools registered yet.</p>
  );

  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {products.map((p) => (
        <li key={p.id}>
          <button
            onClick={() => onSelect(p.id)}
            className="flex w-full items-center gap-3 rounded-md border border-slate-200 bg-white px-5 py-4 text-left shadow-sm transition hover:border-indigo-400 hover:shadow-md"
          >
            <ShieldCheck className="h-5 w-5 flex-shrink-0 text-indigo-500" />
            <div>
              <div className="font-medium text-slate-800">{p.name}</div>
              <div className="text-xs text-slate-400">{p.id}</div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function SignedInBody({ tool }: { tool: string }) {
  const { getToken } = useAuth();
  const { organization, membership, isLoaded } = useOrganization();
  const { setActive } = useClerk();

  // Super-admin detection — happens once on mount.
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [allOrgs, setAllOrgs] = useState<AdminOrg[] | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");

  useEffect(() => {
    adminIsSuperAdmin(getToken)
      .then((yes) => {
        setIsSuperAdmin(yes);
        if (yes) {
          return adminListOrgs(getToken).then(setAllOrgs).catch(() => setAllOrgs([]));
        }
      })
      .catch(() => setIsSuperAdmin(false));
  }, [getToken]);

  // Track whether setActive has been called for the current org so that
  // MemberManager only mounts (and fires its API call) once the session
  // token has been refreshed to include org_id/org_role claims.
  const [activatedOrgId, setActivatedOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (isSuperAdmin !== false) return; // only for regular users
    if (organization?.id && organization.id !== activatedOrgId) {
      void setActive({ organization: organization.id }).then(() => {
        setActivatedOrgId(organization.id);
      });
    }
  }, [organization?.id, activatedOrgId, setActive, isSuperAdmin]);

  // Still loading super-admin status
  if (isSuperAdmin === null) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  // ── Super-admin path ────────────────────────────────────────────────────
  if (isSuperAdmin) {
    const selectedOrg = allOrgs?.find((o) => o.id === selectedOrgId);

    return (
      <div className="space-y-6">
        {/* Org selector */}
        <div className="rounded-md border border-indigo-100 bg-indigo-50 px-5 py-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-600">
            <Building2 className="h-3.5 w-3.5" />
            Editing organisation
          </div>
          <div className="relative">
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full appearance-none rounded-md border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-800 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-50"
              disabled={!allOrgs}
            >
              <option value="">
                {allOrgs ? "Select an organisation…" : "Loading organisations…"}
              </option>
              {allOrgs?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
          {selectedOrg && (
            <p className="mt-1.5 text-xs text-indigo-500">
              Showing access for <span className="font-medium">{selectedOrg.name}</span>
            </p>
          )}
        </div>

        {selectedOrgId ? (
          <MemberManager tool={tool} orgId={selectedOrgId} />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
            <Building2 className="mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              Select an organisation above to manage its access.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Regular org-admin path ──────────────────────────────────────────────
  if (!isLoaded || (organization && activatedOrgId !== organization.id)) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!organization) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
        <ShieldCheck className="mb-3 h-10 w-10 text-slate-400" />
        <h2 className="text-base font-semibold text-slate-700">
          Select an organisation
        </h2>
        <p className="mt-1 mb-4 max-w-sm text-sm text-slate-500">
          Switch to the organisation you want to manage access for.
        </p>
        <OrganizationSwitcher />
      </div>
    );
  }

  const isOrgAdmin = membership?.role === "org:admin";
  if (!isOrgAdmin) {
    return <AdminAccessRequired />;
  }

  return <MemberManager tool={tool} orgId={organization.id} />;
}

function MemberManager({ tool, orgId }: { tool: string; orgId: string }) {
  const { getToken } = useAuth();
  const { user } = useUser();

  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [grantTarget, setGrantTarget] = useState<Member | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Member | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMembers(getToken, tool, orgId);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [getToken, tool, orgId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const callerId = user?.id ?? null;

  const { withAccess, withoutAccess } = useMemo(() => {
    const withAccess: Member[] = [];
    const withoutAccess: Member[] = [];
    for (const m of members ?? []) {
      // Hide the current caller from the action lists — the server
      // refuses self-revoke and a self-grant button would be confusing.
      // Org-admins are still shown in the "with access" section.
      if (m.orgRole === "org:admin" || m.toolRole !== "none") {
        if (m.clerkUserId === callerId && m.orgRole !== "org:admin") {
          // skip non-admin self
        } else {
          withAccess.push(m);
        }
      } else {
        if (m.clerkUserId === callerId) {
          // skip non-admin self
        } else {
          withoutAccess.push(m);
        }
      }
    }
    return { withAccess, withoutAccess };
  }, [members, callerId]);

  return (
    <>
      <RoleLegend />

      {loading && (
        <ul className="rounded-md border border-slate-100">
          <MemberRowSkeleton />
          <MemberRowSkeleton />
          <MemberRowSkeleton />
        </ul>
      )}

      {!loading && error && (
        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Couldn't load members</div>
            <div className="mt-0.5 text-red-700">{error}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && members && (
        <>
          {withAccess.length === 0 && withoutAccess.length === 0 ? (
            <Panel>No non-admin members in your organisation.</Panel>
          ) : (
            <div className="space-y-8">
              <Section
                title="Members with access"
                count={withAccess.length}
                empty="No members currently have access."
              >
                {withAccess.map((m) => (
                  <MemberRow
                    key={m.clerkUserId}
                    member={m}
                    onGrant={setGrantTarget}
                    onRevoke={setRevokeTarget}
                  />
                ))}
              </Section>

              <Section
                title="Members without explicit access"
                count={withoutAccess.length}
                empty="Every member already has an explicit role."
              >
                {withoutAccess.map((m) => (
                  <MemberRow
                    key={m.clerkUserId}
                    member={m}
                    onGrant={setGrantTarget}
                    onRevoke={setRevokeTarget}
                  />
                ))}
              </Section>
            </div>
          )}
        </>
      )}

      <GrantDialog
        open={grantTarget !== null}
        onOpenChange={(o) => {
          if (!o) setGrantTarget(null);
        }}
        member={grantTarget}
        productId={tool}
        orgId={orgId}
        onSuccess={() => void refetch()}
        getToken={getToken}
      />
      <RevokeDialog
        open={revokeTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRevokeTarget(null);
        }}
        member={revokeTarget}
        productId={tool}
        orgId={orgId}
        onSuccess={() => void refetch()}
        getToken={getToken}
      />
    </>
  );
}

function Section({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title} <span className="ml-1 text-slate-400">({count})</span>
      </h2>
      {count === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="rounded-md border border-slate-100">{children}</ul>
      )}
    </section>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted p-6 text-sm">
      {children}
    </div>
  );
}

function AdminAccessRequired() {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
      <ShieldCheck className="mb-3 h-10 w-10 text-slate-400" />
      <h2 className="text-base font-semibold text-slate-700">
        Admin access required
      </h2>
      <p className="mt-1 max-w-sm text-sm text-slate-500">
        This page is only available to organisation admins.
      </p>
    </div>
  );
}
